<template>
  <div
    v-for="item in roomStore.localFurnitureItems"
    :key="item.nanoid"
    class="editable-furniture"
    :style="containerStyle(item)"
    @mousedown.stop="(e) => startDrag(e, item)"
  >
    <furnitureIcon :icon="item.type" :width="item.width" :height="item.length" />
  </div>
</template>

<script setup lang="ts">
import { type CSSProperties } from 'vue'
import type { LocalFurnitureItem } from '@/api/room/types'
import { useRoomStore } from '@/stores/room'

defineOptions({ name: 'BaseRadarEditableFurniture' })
const roomStore = useRoomStore()

interface Props {
  angle: number
  coordinates: [number, number, number, number]
  canvasSize?: number
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canvasSize: 500,
  disabled: false,
})

const containerStyle = (item: LocalFurnitureItem) => {
  const cssObj: CSSProperties = {
    position: 'absolute',
    left: `${item.left}px`,
    top: `${item.top}px`,
    transform: `rotate(${item.rotate}deg)`,
    transformOrigin: 'center center',
    width: `${item.width}px`,
    height: `${item.length}px`,
    pointerEvents: props.disabled ? 'none' : 'auto',
    zIndex: 100,
    cursor: props.disabled ? 'default' : 'move',
  }
  return cssObj
}

// 计算旋转后的四个顶点坐标
const getRotatedCorners = (item: LocalFurnitureItem, left: number, top: number) => {
  const centerX = left + item.width / 2
  const centerY = top + item.length / 2
  const angle = (item.rotate * Math.PI) / 180

  // 四个顶点相对于中心的坐标（未旋转时）
  const corners = [
    { x: -item.width / 2, y: -item.length / 2 }, // 左上
    { x: item.width / 2, y: -item.length / 2 }, // 右上
    { x: item.width / 2, y: item.length / 2 }, // 右下
    { x: -item.width / 2, y: item.length / 2 }, // 左下
  ]

  // 旋转后的顶点坐标
  return corners.map((corner) => {
    const rotatedX = corner.x * Math.cos(angle) - corner.y * Math.sin(angle)
    const rotatedY = corner.x * Math.sin(angle) + corner.y * Math.cos(angle)
    return {
      x: centerX + rotatedX,
      y: centerY + rotatedY,
    }
  })
}

// 精确的边界检查
const checkBoundary = (item: LocalFurnitureItem, left: number, top: number): [number, number] => {
  let newLeft = left
  let newTop = top

  // 获取旋转后的四个顶点
  const corners = getRotatedCorners(item, newLeft, newTop)

  // 计算旋转后的边界框
  const minX = Math.min(...corners.map((c) => c.x))
  const maxX = Math.max(...corners.map((c) => c.x))
  const minY = Math.min(...corners.map((c) => c.y))
  const maxY = Math.max(...corners.map((c) => c.y))

  // 计算需要调整的偏移量
  let offsetX = 0
  let offsetY = 0

  // 左边界检查
  if (minX < 0) {
    offsetX = -minX
  }

  // 右边界检查
  if (maxX > props.canvasSize) {
    offsetX = props.canvasSize - maxX
  }

  // 上边界检查
  if (minY < 0) {
    offsetY = -minY
  }

  // 下边界检查
  if (maxY > props.canvasSize) {
    offsetY = props.canvasSize - maxY
  }

  // 应用偏移量
  newLeft += offsetX
  newTop += offsetY

  return [newLeft, newTop]
}

// 拖拽
const startDrag = (e: MouseEvent, item: LocalFurnitureItem) => {
  if (props.disabled) return
  e.preventDefault()
  e.stopPropagation()

  // 记录初始位置
  const startX = e.clientX
  const startY = e.clientY
  const startLeft = item.left
  const startTop = item.top

  const onMove = (ev: MouseEvent) => {
    // 计算鼠标移动的偏移量
    const deltaX = ev.clientX - startX
    const deltaY = ev.clientY - startY

    // 计算新位置
    const newLeft = startLeft + deltaX
    const newTop = startTop + deltaY

    // 检查边界并应用位置
    const [boundedLeft, boundedTop] = checkBoundary(item, newLeft, newTop)

    // 直接更新 store 中的项目
    const itemIndex = roomStore.localFurnitureItems.findIndex((i) => i.nanoid === item.nanoid)
    if (itemIndex !== -1) {
      roomStore.localFurnitureItems[itemIndex].left = boundedLeft
      roomStore.localFurnitureItems[itemIndex].top = boundedTop

      // 调用处理函数
      roomStore.handlerFurnitureC2R(roomStore.localFurnitureItems[itemIndex])
      console.log('startDrag onMove', boundedLeft, boundedTop)
    }
  }

  const onUp = () => {
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    document.body.style.userSelect = ''
  }

  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
  document.body.style.userSelect = 'none'
}
</script>

<style scoped lang="less">
.editable-furniture {
  position: absolute;
  pointer-events: auto;
  z-index: 100;

  &:hover {
    outline: 2px dashed #1890ff;
    outline-offset: 1px;
  }

  .furniture-rotated-container {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;

    .furniture-wrapper {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      :deep(*) {
        pointer-events: none;
      }

      .text {
        font-size: 14px;
        color: #f41313;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }
}
</style>
